import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { XYCoord } from "dnd-core";

// Based on https://react-dnd.github.io/react-dnd/examples/sortable/simple

export type ReorderHandler = (fromIndex: number, toIndex: number) => void;

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const moveItemInArray = <T>(
  array: T[],
  fromIndex: number,
  toIndex: number,
) => {
  if (fromIndex < toIndex) {
    return [
      ...array.slice(0, fromIndex),
      ...array.slice(fromIndex + 1, toIndex + 1),
      array[fromIndex],
      ...array.slice(toIndex + 1),
    ];
  }

  return [
    ...array.slice(0, toIndex),
    array[fromIndex],
    ...array.slice(toIndex, fromIndex),
    ...array.slice(fromIndex + 1),
  ];
};

const useListReordering = (
  id: string,
  index: number,
  itemType: string,
  onReorder: ReorderHandler,
) => {
  const ref = useRef<HTMLLIElement>(null);
  const [{ isAnyItemDragged }, drop] = useDrop<
    DragItem,
    void,
    { isAnyItemDragged: boolean }
  >({
    accept: itemType,
    collect(monitor) {
      return {
        isAnyItemDragged: monitor.getItem() !== null,
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onReorder(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemType,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return { ref, opacity, isDragging, isAnyItemDragged };
};

export default useListReordering;
